/**
 * Mots de passe fuites en k-anonymat -- point d'entree web du moteur de fuites
 * GALAXIE (`@santvia/breach-core`, ESM portable sans dependance), partage avec
 * Vigilvia : une seule implementation du protocole « Pwned Passwords range »,
 * un seul validateur de point d'acces, un seul score d'exposition.
 *
 * Le point d'acces par defaut est NOTRE **miroir UE** `https://fuites.santvia.eu`
 * (`tools/miroir-fuites.mjs`, meme protocole, aucun journal) ; un deploiement peut
 * en declarer un autre au build (`VITE_BREACH_ENDPOINT`). JAMAIS l'API publique
 * Pwned Passwords (decision du porteur du 2026-10-16) : ni en defaut, ni en repli,
 * ni par un reglage -- `normalizeBreachEndpoint` la refuse.
 */
import { BreachCheckError, SANTVIA_BREACH_ENDPOINT, normalizeBreachEndpoint, isRefusedBreachEndpoint } from '@santvia/breach-core';

export {
  BreachCheckError,
  checkPwnedPasswords,
  computeExposureScore,
  isRefusedBreachEndpoint,
  normalizeBreachEndpoint,
  parseRangeResponse,
  sha1Hex,
} from '@santvia/breach-core';
export type { BreachCheckOptions, CredentialVerdict, ExposureScore } from '@santvia/breach-core';

/** Miroir du deploiement s'il en declare un, notre miroir sinon ; un tiers refuse au build retombe sur notre miroir. */
export const DEFAULT_PWNED_PASSWORDS_ENDPOINT: string = defaultBreachEndpoint(import.meta.env?.VITE_BREACH_ENDPOINT as string | undefined);

function defaultBreachEndpoint(configured: string | undefined): string {
  if (configured === undefined || configured.trim() === '') return SANTVIA_BREACH_ENDPOINT;
  try {
    return normalizeBreachEndpoint(configured);
  } catch {
    return SANTVIA_BREACH_ENDPOINT;
  }
}

/**
 * Reglage enregistre (`klavox:breach-endpoint`) relu au demarrage. `null` = verification desactivee. Un ancien
 * reglage vers l'API publique (accepte avant le 2026-10-16) garde l'accord de l'utilisateur mais passe par notre
 * miroir : `replaced` dit a l'appelant de reecrire le stockage. Une valeur illisible desactive, comme avant.
 */
export function resolveStoredBreachEndpoint(stored: string | null): { readonly endpoint: string | null; readonly replaced: boolean } {
  if (stored === null) return { endpoint: null, replaced: false };
  if (isRefusedBreachEndpoint(stored)) return { endpoint: DEFAULT_PWNED_PASSWORDS_ENDPOINT, replaced: true };
  try {
    return { endpoint: normalizeBreachEndpoint(stored), replaced: false };
  } catch (cause) {
    if (cause instanceof BreachCheckError) return { endpoint: null, replaced: false };
    throw cause;
  }
}
